import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import GroundVehicleMotionModel from './GroundVehicleMotionModel.js';
import AIDriver from './AIDriver.js';
import HitVolume from '../weapons/HitVolume.js';
import { rand, srand } from '../utils.js';

const _emitterWorld = new THREE.Vector3();

// Min/max gap between successive particle puffs (seconds). Used for both the
// rolling-dust kick-up and the damage smoke trails — both effects are sparse
// continuous emitters that read fine at a few Hz and would otherwise dominate
// the particle pool. Randomised per tick so a column doesn't pulse in lockstep.
const EMIT_INTERVAL_MIN = 0.2;
const EMIT_INTERVAL_MAX = 0.4;
// Below this speed the vehicle isn't kicking up enough dust to bother — also
// avoids dust pluming off destroyed wrecks that are coasting to a stop.
const DUST_MIN_SPEED = 1.0;

/**
 * One AI-controlled ground vehicle. Parallels EnemyHelicopter / EnemyPlane —
 * model clone + kinematic motion model + AI driver — with two key differences:
 *
 *   1. Y/pitch/roll are derived from terrain sampling in the motion model;
 *      the vehicle sits on and conforms to the ground.
 *   2. Destruction is non-fatal: the wreck stays in the scene with its model
 *      visible, AI driver is disabled, and the motion model coasts to a halt.
 *      The entity sets `destroyed = true` (not `dead`) so EnemyManager won't
 *      prune it.
 */
export default class EnemyGroundVehicle {
  constructor({
    model, params, position, heading, scene,
    particleSystem, explosionSystem = null, audioManager = null,
    initialHealth = 100,
    initialState = 'explore',
    roadNetwork = null,
  }) {
    this.params = params;
    this.particleSystem = particleSystem;
    this.explosionSystem = explosionSystem;
    this.audioManager = audioManager;
    this.health = initialHealth;
    this.dead = false;          // never set — wrecks stay in scene
    this.destroyed = false;     // true once health hits 0
    this._terrainCollider = null; // cached from update() for the death dust burst
    this.paused = true;         // dormant until EnemyManager clears the takeoff grace

    // Audio tag — feeds the shared driving loop keyed by AUDIO.enemyLoops
    // (AudioManager._updateEnemyLoops), scaled by this vehicle's own speed so a
    // halted or wrecked one falls out of the mix.
    this.audioLoop = 'groundVehicle';

    // RWR tagging — APCs/IFVs carry no radar. Future AAA variants override
    // these flags so the receiver picks them up without further changes.
    this.radarEquipped = false;
    this.missileEquipped = false;

    // AIM-9 is air-to-air — the IR seeker won't acquire cool ground targets.
    this.isGroundTarget = true;

    this.modelRoot = cloneSkeleton(model);
    this.modelRoot.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    this.group = new THREE.Group();
    this.group.add(this.modelRoot);
    scene.add(this.group);

    this.flight = new GroundVehicleMotionModel(params);
    this.flight.position.copy(position);
    this.flight.heading = heading;

    // The network is handed to every vehicle, not just the ones spawned on a
    // road: it's also what a follower uses to rejoin the network on its own if
    // it loses its leader mid-route (AIDriver.promoteToLeader).
    this.pilot = new AIDriver(params, initialState, roadNetwork);

    // Stagger initial emitter timers per-instance so a freshly spawned column
    // doesn't pulse on the same frame.
    this._dustTimer  = rand(EMIT_INTERVAL_MIN, EMIT_INTERVAL_MAX);
    this._smokeTimer = rand(EMIT_INTERVAL_MIN, EMIT_INTERVAL_MAX);

    // Collision volume — same convention as helicopters / planes.
    const hitboxNode = this.modelRoot.getObjectByName('Hitbox');
    if (hitboxNode) {
      if (hitboxNode.isMesh) hitboxNode.visible = false;
      this.hitVolume = new HitVolume(hitboxNode);
    } else {
      this.hitVolume = null;
      console.warn('EnemyGroundVehicle: "Hitbox" node not found in model');
    }

    // Damage smoke emitter anchor on the model.
    this.emitterNode = this.modelRoot.getObjectByName('Emitter');
    if (!this.emitterNode) {
      console.warn('EnemyGroundVehicle: "Emitter" node not found in model');
    }
  }

  update(dt, terrainCollider /*, player */) {
    // Held for the death explosion's dust scatter, which needs ground heights
    // out to a radius and is raised from onProjectileHit rather than update.
    this._terrainCollider = terrainCollider;

    // Dormant during the takeoff grace: skip the driver, dust and smoke, but
    // still run the motion model (desiredSpeed stays 0, so it only settles onto
    // the terrain slope) and keep the hit volume live so it stays targetable.
    if (this.paused) {
      this.flight.update(dt, terrainCollider);
      this.group.position.copy(this.flight.position);
      this.group.quaternion.copy(this.flight.quaternion);
      if (this.hitVolume) this.hitVolume.update();
      return;
    }

    this.pilot.update(dt, this.flight, terrainCollider);
    this.flight.update(dt, terrainCollider);
    this.group.position.copy(this.flight.position);
    this.group.quaternion.copy(this.flight.quaternion);

    if (this.hitVolume) this.hitVolume.update();

    this._updateDust(dt);
    this._updateSmokeTrail(dt);
  }

  // Throttled damage smoke — same exclusive-stage pattern as helicopters /
  // planes for the in-combat stages. Once destroyed, the wreck sits on the
  // ground burning, so we swap to the rising ground-fire variant instead of
  // the wind-trailed aerial fire smoke. Shares the same random-interval
  // cadence as the dust emitter so we don't flood the particle pool.
  _updateSmokeTrail(dt) {
    if (!this.particleSystem || !this.emitterNode) return;
    this._smokeTimer -= dt;
    if (this._smokeTimer > 0) return;
    this._smokeTimer = rand(EMIT_INTERVAL_MIN, EMIT_INTERVAL_MAX);

    this.emitterNode.getWorldPosition(_emitterWorld);
    const v = this.flight.velocity;
    // Vehicles aren't wind-trailed aerial emitters, so the fire stage and the
    // destroyed-wreck stage both use the buoyant ground-fire variant.
    if (this.destroyed || this.health < 40) {
      this.particleSystem.spawnGroundFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
    } else if (this.health < 60) {
      this.particleSystem.spawnBlackSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
    } else if (this.health < 80) {
      this.particleSystem.spawnWhiteSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
    }
  }

  // Throttled dust kick-up — emits a single `dustLarge` particle a few times
  // per second while the vehicle is moving. Carries a fraction of the
  // vehicle's velocity so the puff trails behind, with a small upward kick.
  _updateDust(dt) {
    if (!this.particleSystem) return;
    this._dustTimer -= dt;
    if (this._dustTimer > 0) return;
    this._dustTimer = rand(EMIT_INTERVAL_MIN, EMIT_INTERVAL_MAX);

    const speed = this.flight.forwardSpeed;
    if (speed < DUST_MIN_SPEED) return;

    // Spawn under the vehicle with a small XZ scatter so the dust reads as
    // wheel-kick rather than a single point source. Y matches the model
    // origin (already ~at ground level via yOffset).
    const p = this.flight.position;
    const vx = this.flight.velocity.x;
    const vz = this.flight.velocity.z;
    this.particleSystem._emit(
      'dustLarge',
      p.x + srand() * 1.5,
      p.y,
      p.z + srand() * 1.5,
      // Trail behind us at a fraction of vehicle speed + tiny upward kick.
      -vx * 0.3 + srand() * 0.5,
      rand(0.5, 1.5),
      -vz * 0.3 + srand() * 0.5,
    );
  }

  /**
   * Apply damage from a projectile hit. No-op on already-destroyed wrecks.
   * `source` is the weapon that landed it ('gau8' / 'aim9' / 'agm65'), which
   * only matters on the hit that kills — see _destroy.
   */
  onProjectileHit(_x, _y, _z, damage = 0, source = null) {
    if (this.destroyed) return;
    this.health = Math.max(0, this.health - damage);
    if (this.health <= 0) this._destroy(source);
  }

  // Kill: spawn explosion + audio, disable AI, motion model coasts to a halt.
  // Model stays in the scene; `dead` remains false so EnemyManager doesn't
  // prune the wreck.
  _destroy(source = null) {
    this.destroyed = true;
    this.flight.disabled = true;
    this.pilot.disable();

    if (this.particleSystem) {
      this.particleSystem.spawnExplosion(
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
        { size: 1.2 });
      // Dirt lifted around the wreck — the blast is sitting on the deck, so
      // the fireball's single central kick-up reads far too clean on its own.
      this.particleSystem.spawnGroundDustBurst(
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
        { size: 1.2, terrainCollider: this._terrainCollider });
    }
    // A Maverick kill also gets a filmed fireball on top of the particle blast
    // (SimConfig.EXPLOSION_SPRITES). Gun kills deliberately don't: 30mm walks a
    // vehicle down over several passes, and a 30 m cinematic fireball on
    // whichever round happens to finish it reads as a different weapon.
    if (source === 'agm65') {
      this.explosionSystem?.spawnEvent('vehicleMaverickKill',
        this.flight.position.x, this.flight.position.y, this.flight.position.z);
    }
    if (this.audioManager) {
      this.audioManager.playExplosion(
        'Explosion_Crash',
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
      );
    }
  }

  dispose() {
    this.group.parent?.remove(this.group);
  }
}
